import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * Admin audit log viewer. Read-only — entries are written by the audit
 * helper from every state-changing endpoint and there's deliberately no
 * delete/edit affordance.
 */

interface AuditRow {
  id: string;
  actor_admin_id: number | null;
  actor_email: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: unknown;
  ip: string | null;
  created_at: string;
}

export function Audit() {
  const [events, setEvents] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [targetType, setTargetType] = useState('');
  const [targetId, setTargetId] = useState('');

  async function load() {
    try {
      const qs = new URLSearchParams();
      if (action.trim()) qs.set('action', action.trim());
      if (actor.trim()) qs.set('actor', actor.trim());
      if (targetType.trim()) qs.set('target_type', targetType.trim());
      if (targetId.trim()) qs.set('target_id', targetId.trim());
      qs.set('limit', '200');
      const data = await api<{ events: AuditRow[] }>(`/admin/audit?${qs}`);
      setEvents(data.events);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => { load(); }, []);

  function deepLink(t: AuditRow): React.ReactNode {
    if (!t.target_type || !t.target_id) return '—';
    if (t.target_type === 'tenant') {
      return <Link to={`/tenants/${t.target_id}`}>tenant #{t.target_id}</Link>;
    }
    if (t.target_type === 'admin_user') {
      return <Link to={`/admin-users`}>admin #{t.target_id}</Link>;
    }
    if (t.target_type === 'stripe_event') {
      return <Link to={`/stripe-events`}>{t.target_id}</Link>;
    }
    return <span className="muted">{t.target_type}#{t.target_id}</span>;
  }

  function actionClass(a: string): string {
    if (a.endsWith('.destroy') || a.endsWith('.refund-last') || a.endsWith('.cancel-subscription')) return 'err';
    if (a.endsWith('.suspend') || a.endsWith('.bulk-update')) return 'warn';
    return 'muted';
  }

  return (
    <div>
      <h1>Audit log</h1>
      <p className="subtitle">
        Every state-changing admin action lands here. Records can't be
        modified or deleted from the UI.
      </p>
      {error && <div className="banner err">{error}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="form-row" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <label>Action contains</label>
            <input value={action} onChange={(e) => setAction(e.target.value)}
              placeholder="tenant.destroy" onKeyDown={(e) => { if (e.key === 'Enter') load(); }} />
          </div>
          <div>
            <label>Actor email contains</label>
            <input value={actor} onChange={(e) => setActor(e.target.value)}
              placeholder="craig@" onKeyDown={(e) => { if (e.key === 'Enter') load(); }} />
          </div>
          <div>
            <label>Target type</label>
            <select value={targetType} onChange={(e) => setTargetType(e.target.value)}>
              <option value="">(any)</option>
              <option value="tenant">tenant</option>
              <option value="admin_user">admin_user</option>
              <option value="billing_provider">billing_provider</option>
              <option value="stripe_event">stripe_event</option>
            </select>
          </div>
          <div>
            <label>Target id</label>
            <input value={targetId} onChange={(e) => setTargetId(e.target.value)}
              placeholder="42" onKeyDown={(e) => { if (e.key === 'Enter') load(); }} />
          </div>
          <div className="spacer" />
          <button onClick={() => load()}>↻ Apply</button>
        </div>
      </div>

      {!events ? (
        <div className="muted">Loading…</div>
      ) : events.length === 0 ? (
        <div className="card muted">No audit entries match the current filters.</div>
      ) : (
        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '8px 12px' }}>When</th>
                <th style={{ padding: '8px 12px' }}>Actor</th>
                <th style={{ padding: '8px 12px' }}>Action</th>
                <th style={{ padding: '8px 12px' }}>Target</th>
                <th style={{ padding: '8px 12px' }}>Details</th>
                <th style={{ padding: '8px 12px' }}>IP</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
                  <td className="muted small" style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{e.actor_email}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span className={`pill ${actionClass(e.action)}`}>{e.action}</span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{deepLink(e)}</td>
                  <td className="code small" style={{ padding: '8px 12px', maxWidth: 320, wordBreak: 'break-word' }}>
                    {e.details ? JSON.stringify(e.details) : <span className="muted">—</span>}
                  </td>
                  <td className="muted small code" style={{ padding: '8px 12px' }}>{e.ip ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
